"use client";

import React, { useState } from "react";
import Image from "next/image";
import { Link, usePathname, useRouter } from "@/i18n/routing";
import {
    AppBar,
    Toolbar,
    Button,
    IconButton,
    Box,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Badge,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import LanguageIcon from "@mui/icons-material/Language";

import { useTranslations, useLocale } from "next-intl";

const navItemKeys = [
    { key: "home", path: "/" },
    { key: "service", path: "/service" },
    { key: "aiAgent", path: "/ai-agent", isNew: true },
    { key: "caseStudies", path: "/case-studies" },
    { key: "news", path: "/news" },
    { key: "about", path: "/about" },
    { key: "faq", path: "/faq" },
    { key: "contact", path: "/contact" },
];

const BRAND_COLOR = "#132c4b";
const ACCENT_COLOR = "#F15859";

const Navbar = () => {
    const [mobileOpen, setMobileOpen] = useState(false);
    const pathname = usePathname();
    const router = useRouter();
    const locale = useLocale();
    const t = useTranslations("Navbar");

    const switchLocale = () => {
        const nextLocale = locale === "ja" ? "en" : "ja";
        router.replace(pathname, { locale: nextLocale });
    };

    const navItems = navItemKeys.map(item => ({
        ...item,
        name: t(item.key)
    }));

    const toggleDrawer = () => setMobileOpen((prev) => !prev);

    /* ----------------- Mobile Drawer ----------------- */
    const drawer = (
        <Box sx={{ height: "100%", bgcolor: BRAND_COLOR, color: "white", px: 2 }}>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    py: 2,
                }}
            >
                {/* <Typography fontWeight={800}>AirXpress</Typography> */}
                <Image
                    src="/images/og-logo-white.webp"
                    alt="AirXpress Logo"
                    width={160}
                    height={35}
                    style={{ width: "auto", height: "35px" }}
                    priority={true}
                />
                <IconButton onClick={toggleDrawer} color="inherit" aria-label="Close mobile menu">
                    <CloseIcon />
                </IconButton>
            </Box>

            <List>
                {navItems.map((item) => {
                    const active = pathname === item.path;
                    return (
                        <ListItem key={item.path} disablePadding>
                            <ListItemButton
                                component={Link}
                                href={item.path}
                                sx={{
                                    py: 1.5,
                                    borderRadius: 1,
                                    mb: 0.5,
                                    bgcolor: active ? "rgba(255,255,255,0.12)" : "transparent",
                                    "&:hover": {
                                        bgcolor: "rgba(255,255,255,0.08)",
                                    },
                                }}
                                onClick={toggleDrawer}
                            >
                                <ListItemText
                                    primary={
                                        <Box display="flex" alignItems="center" gap={1}>
                                            {item.name}
                                            {item.isNew && (
                                                <Box
                                                    sx={{
                                                        bgcolor: ACCENT_COLOR,
                                                        color: "white",
                                                        fontSize: "0.6rem",
                                                        fontWeight: 800,
                                                        px: 0.75,
                                                        py: 0.25,
                                                        borderRadius: "4px",
                                                        lineHeight: 1,
                                                        position: "relative",
                                                        "&::after": {
                                                            position: "absolute",
                                                            top: 0,
                                                            left: 0,
                                                            width: "100%",
                                                            height: "100%",
                                                            borderRadius: "4px",
                                                            animation: "ripple 1.2s infinite ease-in-out",
                                                            border: `1px solid ${ACCENT_COLOR}`,
                                                            content: '""',
                                                        },
                                                        "@keyframes ripple": {
                                                            "0%": { transform: "scale(1)", opacity: 1 },
                                                            "100%": { transform: "scale(1.8)", opacity: 0 },
                                                        },
                                                    }}
                                                >
                                                    NEW
                                                </Box>
                                            )}
                                        </Box>
                                    }
                                    primaryTypographyProps={{
                                        fontWeight: active ? 700 : 500,
                                    }}
                                />
                            </ListItemButton>
                        </ListItem>
                    );
                })}

                <Box sx={{ mt: 3, display: "flex", flexDirection: "column", gap: 2 }}>
                    <Button
                        onClick={switchLocale}
                        fullWidth
                        startIcon={<LanguageIcon sx={{ fontSize: '1.2rem !important' }} />}
                        sx={{
                            color: "white",
                            textTransform: "none",
                            fontWeight: 700,
                            fontSize: "1rem",
                            letterSpacing: "0.5px",
                            py: 1.2,
                            bgcolor: "#F15859",
                            border: "1px solid #F15859",
                            borderRadius: 6,
                            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                            "&:hover": {
                                bgcolor: "#F15859",
                                borderColor: "#F15859",
                                transform: "translateY(-2px)",
                                boxShadow: "0 4px 14px rgba(241, 88, 89, 0.4)",
                            },
                            "&:active": {
                                transform: "translateY(0)",
                            }
                        }}
                    >
                        {locale === "ja" ? "English" : "日本語"}
                    </Button>
                </Box>
            </List>
        </Box>
    );

    /* ----------------- Desktop ----------------- */
    return (
        <AppBar
            position="sticky"
            elevation={0}
            sx={{
                bgcolor: "#132c4b",
                backdropFilter: "blur(10px)",
                borderBottom: "1px solid rgba(255,255,255,0.08)",
            }}
        >
            <Toolbar
                sx={{
                    maxWidth: "lg",
                    mx: "auto",
                    width: "100%",
                    px: { xs: 2.5, md: 0 },
                    justifyContent: "space-between",
                }}
            >
                {/* Brand */}
                <Link href="/">
                    <Image
                        src="/images/og-logo-white.webp"
                        alt="AirXpress Logo"
                        width={140}
                        height={31}
                        style={{ width: "auto", height: "31px" }}
                        priority={true}
                    />
                </Link>

                {/* Desktop Nav */}
                <Box sx={{ display: { xs: "none", lg: "flex" }, gap: 3 }}>
                    {navItems.map((item) => {
                        const active = pathname === item.path;
                        return (
                            <Button
                                key={item.path}
                                component={Link}
                                href={item.path}
                                sx={{
                                    color: "white",
                                    textTransform: "none",
                                    fontWeight: active ? 700 : 500,
                                    position: "relative",
                                    opacity: active ? 1 : 0.85,
                                    "&::after": active
                                        ? {
                                            content: '""',
                                            position: "absolute",
                                            bottom: 4,
                                            left: "20%",
                                            width: "60%",
                                            height: "2px",
                                            bgcolor: ACCENT_COLOR,
                                            borderRadius: 1,
                                        }
                                        : {},
                                    "&:hover": {
                                        opacity: 1,
                                        transform: "translateY(-1px)",
                                    },
                                }}
                            >
                                <Box display="flex" alignItems="center" gap={0.75}>
                                    {item.name}
                                    {item.isNew && (
                                        <Box
                                            sx={{
                                                bgcolor: ACCENT_COLOR,
                                                color: "white",
                                                fontSize: "0.55rem",
                                                fontWeight: 800,
                                                px: 0.5,
                                                py: 0.25,
                                                borderRadius: "4px",
                                                lineHeight: 1,
                                                position: "relative",
                                                "&::after": {
                                                    position: "absolute",
                                                    top: 0,
                                                    left: 0,
                                                    width: "100%",
                                                    height: "100%",
                                                    borderRadius: "4px",
                                                    animation: "ripple 1.2s infinite ease-in-out",
                                                    border: `1px solid ${ACCENT_COLOR}`,
                                                    content: '""',
                                                },
                                                "@keyframes ripple": {
                                                    "0%": { transform: "scale(1)", opacity: 1 },
                                                    "100%": { transform: "scale(2)", opacity: 0 },
                                                },
                                            }}
                                        >
                                            NEW
                                        </Box>
                                    )}
                                </Box>
                            </Button>
                        );
                    })}
                </Box>

                {/* Right Actions */}
                <Box sx={{ display: { xs: "none", lg: "flex" }, gap: 2, alignItems: "center" }}>
                    <Button
                        onClick={switchLocale}
                        startIcon={<LanguageIcon sx={{ fontSize: '1.1rem !important' }} />}
                        sx={{
                            color: "white",
                            textTransform: "none",
                            fontWeight: 700,
                            fontSize: "0.85rem",
                            letterSpacing: "0.5px",
                            minWidth: "auto",
                            px: 2.5,
                            py: 0.8,
                            bgcolor: "#F15859",
                            border: "1px solid #F15859",
                            borderRadius: 6,
                            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                            "&:hover": {
                                bgcolor: "#F15859",
                                borderColor: "#F15859",
                                transform: "translateY(-2px)",
                                boxShadow: "0 4px 14px #F15859",
                            },
                            "&:active": {
                                transform: "translateY(0)",
                            }
                        }}
                    >
                        {locale === "ja" ? "EN" : "JP"}
                    </Button>
                </Box>

                {/* Mobile Toggle */}
                <IconButton
                    onClick={toggleDrawer}
                    sx={{ display: { lg: "none" }, color: "white" }}
                    aria-label="Open mobile menu"
                >
                    <MenuIcon />
                </IconButton>
            </Toolbar>

            <Drawer
                open={mobileOpen}
                onClose={toggleDrawer}
                variant="temporary"
                ModalProps={{ keepMounted: true }}
                sx={{
                    display: { xs: "block", lg: "none" },
                    "& .MuiDrawer-paper": {
                        width: 280,
                        bgcolor: BRAND_COLOR,
                    },
                }}
            >
                {drawer}
            </Drawer>
        </AppBar>
    );
};

export default Navbar;
